import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Alert, Box, Button, Chip, CircularProgress, InputAdornment, Stack, TextField, Typography} from '@mui/material';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import PropTypes from 'prop-types';
import {loadStripe} from '@stripe/stripe-js';
import {EmbeddedCheckout, EmbeddedCheckoutProvider} from '@stripe/react-stripe-js';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';
import RequiredCheckbox from './Shared/RequiredCheckbox';
import {
    useCreateFormPaymentIntentMutation,
    useGetFormFieldPaymentQuery,
    useGetFormFieldPaymentStatusQuery,
    useGetFormPaymentStatusQuery,
    useRefundPaymentMutation,
    useVerifyFormPaymentSessionMutation
} from '../../../../../api/services/paymentsService';
import useNotification from '../../../../../utility/hooks/useNotification';
import useUser from '../../../../../utility/hooks/useUser';
import {useConfirm} from 'material-ui-confirm';

const NONE = 0; // PaymentRequestStatus.None
const PAID = 3; // PaymentRequestStatus.Paid
const REFUNDED = 6; // PaymentRequestStatus.Refunded
const STATUS_POLL_MS = 3000;

/** Stripe.js is loaded once per (key, account) pair, not on every render. */
const stripeCache = new Map();

const getStripe = (publishableKey, accountId) => {
    if (!publishableKey) return null;

    const cacheKey = `${publishableKey}|${accountId ?? ''}`;

    if (!stripeCache.has(cacheKey)) {
        stripeCache.set(
            cacheKey,
            loadStripe(publishableKey, accountId ? {stripeAccount: accountId} : undefined)
        );
    }

    return stripeCache.get(cacheKey);
};

const formatPence = (pence) =>
    typeof pence === 'number'
        ? new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP'
        }).format(pence / 100)
        : '';

/**
 * Takes a card payment inside Admicity and records it against the response.
 *
 * This is separate from the Payment component, which sends the payer out to a Stripe or Worldpay
 * link the school pastes in and records nothing. That one is unchanged and still in use; this is an
 * additional question type for schools who have connected their Stripe account.
 *
 * The school sets the amount in the builder. It is never sent from the browser when paying - the
 * server reads it from the form schema - so nothing here can change what is charged.
 */
const StripePayment = ({
                         index,
                         componentId,
                         descriptionHtml,
                         onDescriptionHtmlChange,
                         commitDescriptionHtml,
                         amountPence,
                         value,
                         formRequestId,
                         enableValidation,
                         error: submitError,
                         onChange,
                         isEditMode,
                         isFormArchived,
                         placeholderValues,
                         readOnly,
                         accessLevel,
                         audience,
                         recipientType,
                         linkedToStudents,
                         linkedToEmployees,
                         submitMode
                     }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);
    const [amountText, setAmountText] = useState(
        typeof amountPence === 'number' ? (amountPence / 100).toFixed(2) : ''
    );
    const [amountError, setAmountError] = useState('');

    const [intent, setIntent] = useState(null);
    const [showCheckout, setShowCheckout] = useState(false);
    const [paidJustNow, setPaidJustNow] = useState(false);
    const [error, setError] = useState('');

    const [createIntent, {isLoading: isStartingPayment}] = useCreateFormPaymentIntentMutation();
    const [verifySession] = useVerifyFormPaymentSessionMutation();

    const {data: existingPayment, refetch: refetchPaymentStatus} = useGetFormFieldPaymentStatusQuery(
        {
            formRequestId,
            fieldId: componentId
        },
        {skip: !submitMode || !formRequestId || readOnly}
    );

    const serverStatus = existingPayment?.status;
    const serverKnows = serverStatus !== undefined && serverStatus !== NONE;
    const paid = paidJustNow || (serverKnows ? serverStatus === PAID : value === 'PAID');

    useEffect(() => {
        if (existingPayment?.status === PAID && value !== 'PAID') {
            onChange?.({
                id: componentId,
                value: 'PAID'
            });
        }

        if (serverKnows && serverStatus !== PAID && value === 'PAID') {
            onChange?.({
                id: componentId,
                value: ''
            });
        }
    }, [existingPayment, serverKnows, serverStatus, value, onChange, componentId]);

    const {showErrorNotification, showSuccessNotification} = useNotification();
    const {user} = useUser();
    const confirm = useConfirm();
    const isViewingResponse = !!readOnly && !isEditMode && !!formRequestId && !!user?.hasElevatedAccess?.();

    const {data: fieldPayment, refetch: refetchFieldPayment} = useGetFormFieldPaymentQuery(
        {
            formRequestId,
            fieldId: componentId
        },
        {skip: !isViewingResponse}
    );

    const [refundPayment, {isLoading: isRefunding}] = useRefundPaymentMutation();

    const isRefunded = fieldPayment?.status === REFUNDED;

    const handleRefund = async () => {
        if (!fieldPayment?.paymentRequestId) return;

        const refundable = fieldPayment.refundablePence ?? 0;

        try {
            await confirm({
                title: 'Refund this payment',
                description: `${formatPence(refundable)} will be returned to the payer. This cannot be undone.`,
                confirmationText: 'Refund',
                cancellationText: 'Cancel',
            });
        } catch {
            return;
        }

        try {
            await refundPayment({paymentRequestId: fieldPayment.paymentRequestId}).unwrap();
            showSuccessNotification(`Refunded ${formatPence(refundable)}`);
            refetchFieldPayment();
        } catch (e) {
            showErrorNotification(e?.data?.message ?? e?.data ?? 'The refund could not be completed.');
        }
    };

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const handleFocus = useCallback(() => setIsEditorFocused(true), []);

    const handleAmountChange = (e) => {
        const text = e.target.value;
        setAmountText(text);

        if (!text.trim()) {
            setAmountError('');
            onChange?.({
                index,
                amountPence: null
            });
            return;
        }

        if (!/^\d*\.?\d{0,2}$/.test(text.trim())) {
            setAmountError('Enter an amount in pounds, for example 12.50');
            return;
        }

        const pounds = Number(text);

        if (!Number.isFinite(pounds) || pounds <= 0) {
            setAmountError('Enter an amount greater than zero');
            return;
        }

        setAmountError('');
        onChange?.({
            index,
            amountPence: Math.round(pounds * 100)
        });
    };

    const markPaid = useCallback(() => {
        setPaidJustNow(true);
        refetchPaymentStatus?.();
        setShowCheckout(false);
        onChange?.({
            id: componentId,
            value: 'PAID'
        });
    }, [componentId, onChange, refetchPaymentStatus]);

    const verifiedSessionRef = useRef(null);

    useEffect(() => {
        if (!submitMode || !formRequestId || paid) return;

        const params = new URLSearchParams(window.location.search);

        if (params.get('paid') !== '1') return;

        const sessionId = params.get('session_id');

        if (!sessionId) return;
        if (sessionId !== rememberedSession()) return;
        if (verifiedSessionRef.current === sessionId) return;

        verifiedSessionRef.current = sessionId;

        verifySession({
            formRequestId,
            sessionId
        })
            .unwrap()
            .then((result) => {
                if (result?.status === PAID) markPaid();
            })
            .catch(() => {
                setError('We could not confirm your payment. Please refresh the page.');
                verifiedSessionRef.current = null;
            });
    }, [submitMode, formRequestId, paid, verifySession, markPaid]);

    const shouldPoll = showCheckout && !paid && !!intent?.paymentRequestId && !!formRequestId;

    const {data: polledStatus} = useGetFormPaymentStatusQuery(
        {
            formRequestId,
            paymentRequestId: intent?.paymentRequestId
        },
        {
            skip: !shouldPoll,
            pollingInterval: STATUS_POLL_MS
        }
    );

    useEffect(() => {
        if (shouldPoll && polledStatus?.status === PAID) markPaid();
    }, [shouldPoll, polledStatus, markPaid]);

    const sessionKey = `admicity:paysession:${formRequestId}:${componentId}`;

    const rememberSession = (clientSecret) => {
        try {
            window.sessionStorage.setItem(sessionKey, String(clientSecret).split('_secret')[0]);
        } catch {
            // Without storage the question simply waits for the server to confirm instead.
        }
    };

    const rememberedSession = () => {
        try {
            return window.sessionStorage.getItem(sessionKey);
        } catch {
            return null;
        }
    };

    const handlePay = async () => {
        setError('');

        try {
            const result = await createIntent({
                formRequestId,
                fieldId: componentId,
                returnPath: `${window.location.pathname}${window.location.search}`
            }).unwrap();

            if (result?.status === PAID) {
                markPaid();
                return;
            }

            if (result?.unavailableReason) {
                setError(result.unavailableReason);
                return;
            }

            if (!result?.clientSecret || !result?.publishableKey) {
                setError('Card payments are not available for this form. Please contact the school.');
                return;
            }

            rememberSession(result.clientSecret);
            setIntent(result);
            setShowCheckout(true);
        } catch (e) {
            setError(e?.data?.message ?? 'We could not start the payment. Please try again.');
        }
    };

    const stripePromise = useMemo(
        () => getStripe(intent?.publishableKey, intent?.stripeAccountId),
        [intent?.publishableKey, intent?.stripeAccountId]
    );

    const description = (
        isEditMode
            ? (
                <RichEditor
                    currentDescription={draftDescriptionHtml}
                    setCurrentDescription={handleDraftChange}
                    isFormLive={isFormArchived}
                    accessLevel={accessLevel}
                    sx={{mb: 2}}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                    placeholderValues={placeholderValues}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                />
            )
            : (
                <RichEditorResultText
                    id={componentId}
                    description={descriptionHtml}
                    placeholderValues={placeholderValues}
                    readonly={readOnly}
                    enableValidation={enableValidation}
                    error={!!submitError}
                    sx={{mb: 2}}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                    submitMode={submitMode}
                />
            )
    );

    if (isEditMode) {
        return (
            <Box sx={{mt: 2}}>
                {description}
                <Stack spacing={1} sx={{mt: 1}}>
                    <TextField
                        label="Amount to charge"
                        placeholder="0.00"
                        value={amountText}
                        onChange={handleAmountChange}
                        error={!!amountError}
                        helperText={amountError || 'Everyone filling this form in is charged this amount.'}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">£</InputAdornment>
                        }}
                        sx={{maxWidth: 280}}
                    />
                    <RequiredCheckbox
                        enableValidation={!!enableValidation}
                        index={index}
                        onChange={onChange}
                        isFormArchived={isFormArchived}
                        text="Required? (the form cannot be submitted until this is paid)"
                    />
                </Stack>
            </Box>
        );
    }

    return (
        <Box sx={{mt: 2}}>
            {description}

            {paid && (
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{mt: 1}}>
                    {isRefunded
                        ? (
                            <Chip
                                color="warning"
                                icon={<UndoOutlinedIcon/>}
                                label={`Refunded ${formatPence(fieldPayment.refundedPence || amountPence)}`}
                            />
                        )
                        : (
                            <>
                                <Chip
                                    color="success"
                                    icon={<CheckCircleOutlineIcon/>}
                                    label={amountPence ? `Paid ${formatPence(amountPence)}` : 'Paid'}
                                />
                                {isViewingResponse && (fieldPayment?.refundedPence ?? 0) > 0 && (
                                    <Chip
                                        color="warning"
                                        variant="outlined"
                                        label={`Part refunded ${formatPence(fieldPayment.refundedPence)}`}
                                    />
                                )}
                            </>
                        )}

                    {/* Only shown while there is something left to give back. */}
                    {isViewingResponse && (fieldPayment?.refundablePence ?? 0) > 0 && (
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={isRefunding ? <CircularProgress size={14}/> : <UndoOutlinedIcon/>}
                            onClick={handleRefund}
                            disabled={isRefunding}
                        >
                            {isRefunding
                                ? 'Refunding…'
                                : `Refund ${formatPence(fieldPayment.refundablePence)}`}
                        </Button>
                    )}

                    {isViewingResponse && fieldPayment?.paidAt && (
                        <Typography variant="caption" color="text.secondary">
                            Paid {new Date(fieldPayment.paidAt).toLocaleString('en-GB')}
                        </Typography>
                    )}
                </Stack>
            )}

            {!paid && !!error && (
                <Alert severity="warning" sx={{
                    mt: 1,
                    mb: 1
                }}>{error}</Alert>
            )}

            {/* Raised by the submit attempt, when this payment is required and has not been made. */}
            {!paid && !error && !!submitError && (
                <Alert severity="error" sx={{
                    mt: 1,
                    mb: 1
                }}>{typeof submitError === 'string' ? submitError : 'This payment must be completed before the form can be submitted'}</Alert>
            )}

            {!paid && !showCheckout && (
                <Stack direction="row" spacing={2} alignItems="center" sx={{mt: 1}}>
                    <Button
                        variant="contained"
                        startIcon={isStartingPayment ? <CircularProgress size={16}/> : <PaymentOutlinedIcon/>}
                        onClick={handlePay}
                        disabled={readOnly || isStartingPayment || !formRequestId}
                    >
                        {amountPence ? `Pay ${formatPence(amountPence)}` : 'Make payment'}
                    </Button>
                    {!formRequestId && (
                        <Typography variant="caption" color="text.secondary">
                            Payment is available once the form has been opened from your invitation.
                        </Typography>
                    )}
                </Stack>
            )}

            {!paid && showCheckout && intent?.clientSecret && stripePromise && (
                <Box sx={{
                    mt: 2,
                    maxWidth: 560
                }}>
                    <EmbeddedCheckoutProvider
                        stripe={stripePromise}
                        options={{clientSecret: intent.clientSecret}}
                    >
                        <EmbeddedCheckout/>
                    </EmbeddedCheckoutProvider>
                </Box>
            )}
        </Box>
    );
};

StripePayment.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func.isRequired,
    commitDescriptionHtml: PropTypes.func.isRequired,
    amountPence: PropTypes.number,
    value: PropTypes.string,
    formRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    enableValidation: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.object]),
    index: PropTypes.number,
    onChange: PropTypes.func,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readOnly: PropTypes.bool,
    accessLevel: PropTypes.number,
    placeholderValues: PropTypes.object,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    linkedToEmployees: PropTypes.bool,
    submitMode: PropTypes.bool
};

export default StripePayment;
